import { z } from "zod/v4";
export const DentalChartSchemaVersion = 1;
const knowledgeSchema = (value) => z.discriminatedUnion("state", [
    z.object({ state: z.literal("confirmed"), value }),
    z.object({ state: z.literal("ambiguous"), value: z.array(value).min(1) }),
    z.object({ state: z.literal("unknown") }),
]);
const importedSourceSchema = z.object({
    sourceId: z.string().min(1),
    recordIdentity: z.string().min(1),
    toothSlot: z.number().int().min(0).max(31).nullable(),
});
const toothSurfaceSchema = z.enum([
    "occlusal",
    "mesial",
    "distal",
    "lingual",
    "vestibular",
]);
const fdiSchema = z.string().regex(/^([1-4][1-8]|[5-8][1-5])$/);
export const DentalEntitySchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("tooth"),
        id: z.string().min(1),
        positionId: z.string().min(1),
        dentition: z.enum(["permanent", "deciduous"]),
        fdi: fdiSchema.nullable(),
        presence: knowledgeSchema(z.enum(["present", "absent"])),
        source: importedSourceSchema.nullable(),
    }),
    z.object({
        kind: z.literal("implant"),
        id: z.string().min(1),
        positionId: z.string().min(1),
        source: importedSourceSchema.nullable(),
    }),
    z.object({
        kind: z.literal("crown"),
        id: z.string().min(1),
        supportId: z.string().min(1),
        source: importedSourceSchema.nullable(),
    }),
    z.object({
        kind: z.literal("restoration"),
        id: z.string().min(1),
        supportId: z.string().min(1),
        surfaces: z.array(toothSurfaceSchema),
        source: importedSourceSchema.nullable(),
    }),
]);
export const DentalObservationSchema = z.object({
    id: z.string().min(1),
    targetId: z.string().min(1),
    surfaces: z.array(toothSurfaceSchema),
    status: knowledgeSchema(z.string().min(1)),
    source: importedSourceSchema.nullable(),
});
export const DentalChartDocumentSchema = z
    .object({
    schemaVersion: z.literal(DentalChartSchemaVersion),
    entities: z.array(DentalEntitySchema),
    observations: z.array(DentalObservationSchema).default([]),
    annotations: z
        .array(z.object({
        id: z.string().min(1),
        targetId: z.string().min(1).nullable(),
        text: z.string(),
        source: importedSourceSchema.nullable(),
    }))
        .default([]),
    importedMarks: z
        .array(z.object({
        id: z.string().min(1),
        targetId: z.string().min(1),
        sourceField: z.string().min(1),
        rawValue: z.string(),
        interpretation: knowledgeSchema(z.string().min(1)),
        source: importedSourceSchema,
    }))
        .default([]),
})
    .superRefine((document, context) => {
    const ids = new Set();
    for (const entity of document.entities) {
        if (ids.has(entity.id)) {
            context.addIssue({
                code: "custom",
                message: `Duplicate entity identifier ${entity.id}`,
                path: ["entities"],
            });
        }
        ids.add(entity.id);
        if (entity.kind === "tooth" &&
            entity.fdi !== null &&
            ((entity.dentition === "permanent" && !/^[1-4][1-8]$/.test(entity.fdi)) ||
                (entity.dentition === "deciduous" && !/^[5-8][1-5]$/.test(entity.fdi)))) {
            context.addIssue({
                code: "custom",
                message: "FDI does not match the dentition",
                path: ["entities"],
            });
        }
    }
    for (const entity of document.entities) {
        if ((entity.kind === "crown" || entity.kind === "restoration") && !ids.has(entity.supportId)) {
            context.addIssue({
                code: "custom",
                message: `Unknown support ${entity.supportId}`,
                path: ["entities"],
            });
        }
    }
    for (const observation of document.observations) {
        if (!ids.has(observation.targetId)) {
            context.addIssue({
                code: "custom",
                message: `Unknown observation target ${observation.targetId}`,
                path: ["observations"],
            });
        }
    }
});
//# sourceMappingURL=DentalChartSchemas.js.map